import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  orderBy,
} from "firebase/firestore";

export default function Customers() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const defaultForm = {
    name: "",
    mobile: "",
    whatsapp: "",
    sameAsMobile: true,
    location: "",
    email: "",
    type: "Regular",
  };

  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState("");

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }
      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "restocustomers"),
      where("propertyId", "==", propertyId),
      orderBy("created", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setCustomers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    if (!form.name.trim()) {
      Toast.fire({ icon: "error", title: "Customer Name Required" });
      return false;
    }

    if (!/^\d{10}$/.test(form.mobile)) {
      Toast.fire({ icon: "error", title: "Mobile must be 10 digits" });
      return false;
    }

    if (!form.sameAsMobile && form.whatsapp && !/^\d{10}$/.test(form.whatsapp)) {
      Toast.fire({ icon: "error", title: "WhatsApp must be 10 digits" });
      return false;
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      Toast.fire({ icon: "error", title: "Invalid Email Address" });
      return false;
    }

    return true;
  };

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!validateForm()) return;

    await addDoc(collection(db, "restocustomers"), {
      ...form,
      propertyId,
      created: Timestamp.now(),
    });

    setForm(defaultForm);
    setOpenAdd(false);
    Toast.fire({ icon: "success", title: "Customer Added" });
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    if (!validateForm()) return;

    await updateDoc(doc(db, "restocustomers", editId), {
      ...form,
      updatedAt: Timestamp.now(),
    });

    setOpenEdit(false);
    Toast.fire({ icon: "success", title: "Customer Updated" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Customer?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "restocustomers", id));
    Toast.fire({ icon: "success", title: "Customer Deleted" });
  };

  /* ================= MOBILE CHANGE ================= */
  const handleMobileChange = (value) => {
    const onlyNumbers = value.replace(/\D/g, "");
    setForm((prev) => ({
      ...prev,
      mobile: onlyNumbers,
      whatsapp: prev.sameAsMobile ? onlyNumbers : prev.whatsapp,
    }));
  };

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: 4 }}>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Customer Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setForm(defaultForm);
                setOpenAdd(true);
              }}
            >
              Add Customer
            </Button>
          </Stack>

          <TextField
            size="small"
            label="Search Customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>WhatsApp</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No Data Found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((row, i) => (
                    <TableRow key={row.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.mobile}</TableCell>
                      <TableCell>{row.whatsapp}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setEditId(row.id);
                            setForm({ ...defaultForm, ...row });
                            setOpenEdit(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* DIALOG */}
      <Dialog
        open={openAdd || openEdit}
        onClose={() => {
          setOpenAdd(false);
          setOpenEdit(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {openAdd ? "Add Customer" : "Edit Customer"}
        </DialogTitle>
        <DialogContent>

          <TextField
            select
            fullWidth
            margin="dense"
            label="Customer Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            <MenuItem value="Regular">Regular</MenuItem>
            <MenuItem value="Special">Special</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Customer Name"
            margin="dense"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Mobile"
            margin="dense"
            value={form.mobile}
            onChange={(e) => handleMobileChange(e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.sameAsMobile}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sameAsMobile: e.target.checked,
                    whatsapp: e.target.checked ? form.mobile : "",
                  })
                }
              />
            }
            label="WhatsApp Same as Mobile"
          />

          <TextField
            fullWidth
            label="WhatsApp Number"
            margin="dense"
            disabled={form.sameAsMobile}
            value={form.whatsapp}
            onChange={(e) =>
              setForm({
                ...form,
                whatsapp: e.target.value.replace(/\D/g, ""),
              })
            }
          />

          <TextField
            fullWidth
            label="Email"
            margin="dense"
            value={form.email}
            onChange={(e) =>
              setForm({ ...form, email: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Location"
            margin="dense"
            value={form.location}
            onChange={(e) =>
              setForm({ ...form, location: e.target.value })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenAdd(false);
              setOpenEdit(false);
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={openAdd ? handleAdd : handleUpdate}
          >
            {openAdd ? "Save" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}